import React, { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import AuthorizeRoute from './authentication/AuthorizeRoute';
import {
  ApplicationPaths,
  LoginActions,
  LogoutActions
} from './authentication/AuthorizationConstants';
import Login from './authentication/Login';
import Logout from './authentication/Logout';
// Lazy load the routes to support code splitting
const Home = lazy(() => import('./modules/home/components/Home'));
const CoreUI = lazy(() => import('./modules/core/components/CoreUI'));
const ControlPanelConfigurationRoutes = lazy(() =>
  import('./modules/configuration/controlpanel/components/ControlPanelConfigurationRoutes')
);
const GoalWorkspaceRoutes = lazy(() => import('./modules/goals/components/GoalWorkspaceRoutes'));

// Commenting the activity builder route as we are retiring activity builder pages.
/*
const ActivityBuilderRoutes = lazy(() =>
  import('./modules/learning/activityBuilder/components/ActivityBuilderRoutes')
);
*/

const ActivityAssignments = lazy(() =>
  import('./modules/learning/activityAssignments/components/ActivityAssignments')
);
const ToDoListConfiguration = lazy(() =>
  import('./modules/configuration/activitiesAndTasks/components/MyActivitiesAndTasksConfiguration')
);
const ReportingRoutes = lazy(() => import('./modules/reporting/ReportingRoutes'));
const DLQExceptions = lazy(() => import('./modules/deadletterqueue/components/DLQExceptions'));
const MyToDoList = lazy(() =>
  import('./modules/onePlace/activitiesAndTasks/activities/components/MyActivitiesRoutes')
);

const AccessDeniedPage = lazy(() => import('./common/layout/components/AccessDeniedPage'));

const UserManagement = lazy(() =>
  import('./modules/administration/users/components/UserManagement')
);
const OfferRoutes = lazy(() => import('./modules/recruiting/offer/components/route/OfferRoutes'));
const AllApplicants = lazy(() =>
  import('./modules/recruiting/allApplicants/components/AllApplicantsView')
);
const PensSettings = lazy(() => import('./modules/learning/pens/components/settings'));
const MicrosoftTeamsSettings = lazy(() =>
  import('./modules/learning/vilt/msTeams/components/msTeamsSettings')
);
const ContentPlayerSettings = lazy(() =>
  import('./modules/learning/activityManagement/components/contentPlayerSettings')
);
const ZoomSettings = lazy(() => import('./modules/learning/vilt/zoom/components/zoomSettings'));
const IntegrationSettings = lazy(() =>
  import('./modules/learning/vivaLearning/components/integrationSettings')
);
const AccreditationRoutes = lazy(() =>
  import('./modules/learning/accreditation/components/AccreditationRoutes')
);
const LateCompletionsListRoutes = lazy(() =>
  import('./modules/learning/latecompletions/components/LateCompletionsListRoutes')
);

const AchievementTemplateRoutes = lazy(() =>
  import(
    './modules/administration/development/developmentplan/achievement/components/AchievementTemplateRoutes'
  )
);
const MarketComparisonRoutes = lazy(() =>
  import('./modules/marketComparison/components/MarketComparisonRoutes')
);

const CredlyConfiguration = lazy(() =>
  import('./modules/configuration/backPackServer/credly/components/CredlyConfiguration')
);
const AssignmentApprovalsRoutes = lazy(() =>
  import('./modules/learning/assignmentapprovals/components/AssignmentApprovalsList')
);
const QuestionBankRoutes = lazy(() =>
  import('./modules/learning/questionBanks/components/QuestionBankRoutes')
);
const InitiaivesSettings = lazy(() =>
  import('./modules/configuration/InitiativesSettings/components/initiativesSettings')
);
const DashboardManagerRoutes = lazy(() =>
  import('./modules/dashboards/components/DashboardManagerRoutes')
);
const DashboardRoutes = lazy(() => import('./modules/dashboards/components/DashboardRoutes'));
const ActivityQrAttendanceReport = lazy(() =>
  import('./modules/learning/activityQrAttendance/components/ActivityQrAttendanceReport')
);
const UserActivityQrCodeAttendanceDetails = lazy(() =>
  import('./modules/learning/activityQrAttendance/components/UserActivityQrCodeAttendanceDetails')
);
const contentConsumerLaunchSetting = lazy(() =>
  import('./modules/learning/contentIntegrationLaunchSettings/components/ContentConsumerSetting')
);
const ViltAttendanceSyncWizard = lazy(() =>
  import('./modules/learning/vilt/attendanceSync/components/AttendanceSyncWizard')
);
const ActivityDetailsRoutes = lazy(() =>
  import('./modules/learning/activityDetails/components/ActivityDetailsRoutes')
);
const VerbFilterSettingsRoutes = lazy(() =>
  import('./modules/learning/contentEngineSettings/components/verbFilter')
);

const AppRoutes = () => {
  return (
    <Routes>
      {/* BEGIN - routes required for oidc-client SSO */}
      {/* Don't add any new routes above this block */}
      {/* Note - add * in the Route component path if the route contains child routes. */}
      {/* Note - For the AuthorizeRoute component path pass only the base route. */}
      <Route path={ApplicationPaths.Login} element={<Login action={LoginActions.Login} />} />
      <Route
        path={ApplicationPaths.LoginCallback}
        element={<Login action={LoginActions.LoginCallback} />}
      />
      <Route path={ApplicationPaths.Logout} element={<Logout action={LogoutActions.Logout} />} />
      {/* END - oidc-client SSO routes */}
      <Route
        path="/accessDenied"
        element={<AuthorizeRoute path="/accessDenied" component={AccessDeniedPage} />}
      />
      <Route
        path="/accreditation/*"
        element={<AuthorizeRoute path="/accreditation" component={AccreditationRoutes} />}
      />
      {/* 
      Commenting the below route as we are retiring Activity Builder pages
      <Route
        path="/activitybuilder/*"
        element={<AuthorizeRoute path="/activitybuilder" component={ActivityBuilderRoutes} />}
      />
      */}
      <Route
        path="/achtemplates/*"
        element={<AuthorizeRoute path="/achtemplates" component={AchievementTemplateRoutes} />}
      />
      <Route
        path="/admin/activity/:activityId/:offeringTargetId/assignments/*"
        element={
          <AuthorizeRoute
            path="/admin/activity/:activityId/:offeringTargetId/assignments"
            component={ActivityAssignments}
          />
        }
      />
      <Route
        path="/admin/activity/:activityId/assignments/*"
        element={
          <AuthorizeRoute
            path="/admin/activity/:activityId/assignments"
            component={ActivityAssignments}
          />
        }
      />
      <Route
        path="/controlpanelConfiguration/*"
        element={
          <AuthorizeRoute
            path="/controlpanelConfiguration"
            component={ControlPanelConfigurationRoutes}
          />
        }
      />
      <Route
        path="/admin/jobs/marketComparison/*"
        element={
          <AuthorizeRoute path="/admin/jobs/marketComparison" component={MarketComparisonRoutes} />
        }
      />
      <Route
        path="/assignmentapprovals"
        element={
          <AuthorizeRoute path="/assignmentapprovals" component={AssignmentApprovalsRoutes} />
        }
      />
      <Route
        path="/credlySettings"
        element={<AuthorizeRoute path="/credlySettings" component={CredlyConfiguration} />}
      />
      <Route
        path="/goals/*"
        element={<AuthorizeRoute path="/goals" component={GoalWorkspaceRoutes} />}
      />
      <Route
        path="/mylearningconfiguration"
        element={
          <AuthorizeRoute path="/mylearningconfiguration" component={ToDoListConfiguration} />
        }
      />
      <Route
        path="/reporting/*"
        element={<AuthorizeRoute path="/reporting" component={ReportingRoutes} />}
      />
      <Route path="/dlq/*" element={<AuthorizeRoute path="/dlq" component={DLQExceptions} />} />
      <Route
        path="/mylearning/*"
        element={<AuthorizeRoute path="/mylearning" component={MyToDoList} />}
      />
      <Route
        path="/administration/users/userManagement"
        element={
          <AuthorizeRoute path="/administration/users/userManagement" component={UserManagement} />
        }
      />
      <Route
        path="/recruiting/offer/*"
        element={<AuthorizeRoute path="/recruiting/offer" component={OfferRoutes} />}
      />
      <Route
        path="/recruiting/allApplicants"
        element={<AuthorizeRoute path="/recruiting/allApplicants" component={AllApplicants} />}
      />
      <Route path="/pens" element={<AuthorizeRoute path="/pens" component={PensSettings} />} />
      <Route
        path="/msTeams"
        element={<AuthorizeRoute path="/msTeams" component={MicrosoftTeamsSettings} />}
      />
      <Route
        path="/ContentPlayerSettings"
        element={<AuthorizeRoute path="/ContentPlayerSettings" component={ContentPlayerSettings} />}
      />
      <Route
        path="/questionBanks/*"
        element={<AuthorizeRoute path="/questionBanks" component={QuestionBankRoutes} />}
      />
      <Route path="/zoom" element={<AuthorizeRoute path="/zoom" component={ZoomSettings} />} />
      <Route
        path="/learn/VivaLearnIntegrationSettings"
        element={
          <AuthorizeRoute
            path="/learn/VivaLearnIntegrationSettings"
            component={IntegrationSettings}
          />
        }
      />
      <Route
        path="/initiativesSettings"
        element={<AuthorizeRoute path="/initiativesSettings" component={InitiaivesSettings} />}
      />
      <Route
        path="/activityqrattendance/report"
        element={
          <AuthorizeRoute
            path="/activityqrattendance/report"
            component={ActivityQrAttendanceReport}
          />
        }
      />
      <Route
        path="/useractivityqrcodeattendancedetails"
        element={
          <AuthorizeRoute
            path="/useractivityqrcodeattendancedetails"
            component={UserActivityQrCodeAttendanceDetails}
          />
        }
      />
      <Route
        path="/learn/consumerIntegrationLaunchSettings"
        element={
          <AuthorizeRoute
            path="/learn/consumerIntegrationLaunchSettings"
            component={contentConsumerLaunchSetting}
          />
        }
      />
      <Route
        path="/dashboard/*"
        element={<AuthorizeRoute path="/dashboard" component={DashboardRoutes} />}
      />
      <Route
        path="/dashboardManager/*"
        element={<AuthorizeRoute path="/dashboardManager" component={DashboardManagerRoutes} />}
      />
      <Route
        path="/latecompletions/*"
        element={<AuthorizeRoute path="/latecompletions" component={LateCompletionsListRoutes} />}
      />
      <Route
        path="/vilt/attendanceSync"
        element={
          <AuthorizeRoute path="/vilt/attendanceSync" component={ViltAttendanceSyncWizard} />
        }
      />
      <Route
        path="/activitydetails/*"
        element={<AuthorizeRoute path="/activitydetails" component={ActivityDetailsRoutes} />}
      />
      <Route
        path="/verbfiltersettings"
        element={<AuthorizeRoute path="/verbfiltersettings" component={VerbFilterSettingsRoutes} />}
      />
      <Route path="/c/*" element={<AuthorizeRoute path="/c" component={CoreUI} />} />
      <Route path="/*" element={<AuthorizeRoute path="/" component={Home} />} />
    </Routes>
  );
};

export default AppRoutes;
